import { Share } from '@capacitor/share';
import {
  DEFAULT_LANGUAGE, WORD_LENGTH, maxGuessesFor,
  type EvaluatedGuess, type GameMode, type Language, type TileState,
} from '@lexicon/core';
import { isNative } from './platform';

const EMOJI: Record<TileState, string> = {
  absent: '⬛',
  present: '🟨',
  correct: '🟩',
};

export interface ShareParams {
  mode: GameMode;
  /** `dayNumber` for dailies, the code for challenges. */
  id: string | number;
  guesses: readonly EvaluatedGuess[];
  won: boolean;
  /**
   * Answer length (spec §10.1). Every daily and journey share carries this
   * as a `· NL ·` tag in its header (spec §10.2) — including the bundled
   * 5-letter game, which now reads `· 5L ·` rather than omitting the tag.
   * The default of `WORD_LENGTH` only spares a caller that is already on
   * that length from having to pass it explicitly; it is not a "no tag"
   * mode. Challenge headers never show a length tag: a challenge's length
   * is already implied by the grid its recipient plays.
   */
  length?: number;
  /**
   * The language the game was played in. Only a non-default language adds
   * a tag to the header (`· NL ·`): an English share text is byte-for-byte
   * what it has always been, so two English players' grids still line up
   * exactly as before. Two players *are* comparing different puzzles when
   * their languages differ — same day, same length, different word — which
   * is what the tag exists to say.
   */
  language?: Language;
}

/** Emoji summary. Contains no letters — the answer never leaks (spec §5). */
export function shareText({
  mode, id, guesses, won, length = WORD_LENGTH, language = DEFAULT_LANGUAGE,
}: ShareParams): string {
  const score = won ? String(guesses.length) : 'X';
  const rows = maxGuessesFor(length);
  const tag = language === DEFAULT_LANGUAGE ? '' : ` ${language.toUpperCase()} ·`;
  // Pre-epoch dailies (spec §3.3) carry a negative dayNumber — "Lexicon
  // #-6" reads as a typo, not a day count, so the number is dropped
  // entirely rather than shown signed (M3).
  // Spec §10.2: the daily's header carries the ladder length, because two
  // players on the same day can be comparing a 4-letter and a 7-letter
  // grid otherwise. The journey branch carries it for the same reason, but
  // has no caller yet — Task 6 wires the journey screen that will use it.
  // Its exact shape is pinned by a test in share.test.ts ('renders the
  // journey header') so that task inherits a ratified format instead of
  // guessing one. Challenge headers keep their old shape — a challenge's
  // length is already implied by the grid its recipient plays.
  const header = mode === 'daily'
    ? (typeof id === 'number' && id < 0
      ? `Lexicon ·${tag} ${length}L · ${score}/${rows}`
      : `Lexicon #${id} ·${tag} ${length}L · ${score}/${rows}`)
    : mode === 'journey'
      ? `Lexicon Journey ·${tag} ${length}L · ${score}/${rows}`
      : `Lexicon Challenge ${id} ${score}/${rows}`;
  const grid = guesses.map((g) => g.tiles.map((t) => EMOJI[t]).join('')).join('\n');
  return `${header}\n\n${grid}`;
}

export type ShareOutcome = 'shared' | 'copied' | 'cancelled' | 'failed';

/** Sheet title on platforms that show one; iOS ignores it for plain text. */
const SHARE_TITLE = 'Lexicon';

/**
 * iOS reports a dismissed share sheet as a *rejection* ("Share canceled")
 * rather than a resolved no-op, so a cancel has to be told apart from a real
 * failure by inspecting the error — the same distinction the Web Share path
 * makes with `AbortError`.
 */
function isCancellation(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return error.name === 'AbortError' || /cancel/i.test(error.message);
}

async function shareNative(text: string): Promise<ShareOutcome> {
  try {
    await Share.share({ title: SHARE_TITLE, text });
    return 'shared';
  } catch (error) {
    // No clipboard fallback here: on iOS the sheet is the whole share UI, so
    // silently copying after it failed would leave the player with a
    // "Copied to clipboard" toast they never asked for.
    return isCancellation(error) ? 'cancelled' : 'failed';
  }
}

/**
 * Web Share first, clipboard second. A user-cancelled native share is
 * `cancelled` and must stay silent; everything else reports so the caller
 * can toast.
 */
export async function shareResult(text: string, nav: Navigator = navigator): Promise<ShareOutcome> {
  if (isNative()) return shareNative(text);
  if (typeof nav.share === 'function') {
    try {
      await nav.share({ text });
      return 'shared';
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return 'cancelled';
      // fall through to the clipboard
    }
  }
  try {
    await nav.clipboard.writeText(text);
    return 'copied';
  } catch {
    return 'failed';
  }
}
